/**
 * Voice conversation for website Knowledge agents (`campaignData.voiceConfig`). Saved with the
 * agent and served to the card when its template is Smart Chat Aurora · Voice.
 *
 * - startWith: 'talk' (a big talk button) | 'type' (today's card with a mic in the message box)
 * - speakAnswers: 'voice' (read aloud when the visitor talks) | 'listen' (only on Listen)
 * - lang: the voice answers are read in, one per campaign language
 * - welcomeText: what the card says on the first tap. null: the card's built-in welcome;
 *   '': no welcome; any other text is said as written
 */
export const VOICE_CONFIG_ON = Object.freeze({
  isActive: true,
  startWith: 'talk',
  speakAnswers: 'voice',
  lang: 'en-GB',
  welcomeText: null,
});

/** Agents saved before voice existed, and requests without voiceConfig. */
export const VOICE_CONFIG_OFF = Object.freeze({ ...VOICE_CONFIG_ON, isActive: false });

/**
 * The voices, each with the campaign languages (src/constants/app/allLanguages.js) it is the
 * starting voice for. English and UK English both start on English (UK); English (US) is only
 * ever picked by hand.
 */
export const VOICE_LANGUAGES = [
  { value: 'en-US', label: 'English (US)', campaignLanguages: [] },
  { value: 'en-GB', label: 'English (UK)', campaignLanguages: ['en', 'en-uk'] },
  { value: 'es-ES', label: 'Spanish', campaignLanguages: ['es'] },
  { value: 'da-DK', label: 'Danish', campaignLanguages: ['da'] },
  { value: 'nl-NL', label: 'Dutch', campaignLanguages: ['nl'] },
  { value: 'lv-LV', label: 'Latvian', campaignLanguages: ['lv'] },
  { value: 'uk-UA', label: 'Ukrainian', campaignLanguages: ['ua'] },
  { value: 'ca-ES', label: 'Catalan', campaignLanguages: ['cat'] },
  { value: 'et-EE', label: 'Estonian', campaignLanguages: ['est'] },
  { value: 'cs-CZ', label: 'Czech', campaignLanguages: ['cz'] },
  { value: 'he-IL', label: 'Hebrew', campaignLanguages: ['he'] },
  { value: 'ru-RU', label: 'Russian', campaignLanguages: ['ru'] },
  { value: 'de-DE', label: 'German', campaignLanguages: ['de'] },
  { value: 'el-GR', label: 'Greek', campaignLanguages: ['el'] },
  { value: 'bg-BG', label: 'Bulgarian', campaignLanguages: ['bg'] },
  { value: 'fr-FR', label: 'French', campaignLanguages: ['fr'] },
  { value: 'vi-VN', label: 'Vietnamese', campaignLanguages: ['vi'] },
  { value: 'it-IT', label: 'Italian', campaignLanguages: ['it'] },
];

/** The voice for a campaign language (`visualization.language`); the default voice otherwise. */
export const voiceForCampaignLanguage = language =>
  VOICE_LANGUAGES.find(option => option.campaignLanguages.includes(language))?.value ??
  VOICE_CONFIG_ON.lang;

/** The card's built-in welcome, said on the first tap when welcomeText was never set. */
export const VOICE_DEFAULT_WELCOME =
  "Hi, I'm the event assistant. Ask me anything about the event.";

export const VOICE_WELCOME_MAX_LENGTH = 300;

/** The line "Hear it" plays when the welcome message is empty. */
export const VOICE_SAMPLE_LINE =
  "Hi, I'm the Expo assistant. Ask me anything about B2B Connect Expo.";

/** The stored config with every field filled in, so the API always gets the full shape. */
export const normalizeVoiceConfig = voiceConfig => ({
  ...VOICE_CONFIG_OFF,
  ...(voiceConfig && typeof voiceConfig === 'object' ? voiceConfig : {}),
  isActive: voiceConfig?.isActive === true,
});

export const getVoiceLanguageLabel = lang =>
  VOICE_LANGUAGES.find(option => option.value === lang)?.label ?? lang;

/**
 * One line for the Tools row, the Publish summary and the overview: how the card opens, how
 * answers are spoken, and the voice. "Off" when voice is off.
 */
export const voiceSummary = voiceConfig => {
  if (!voiceConfig?.isActive) return 'Off';
  const opens = voiceConfig.startWith === 'type' ? 'Type first' : 'Talk first';
  const reads = voiceConfig.speakAnswers === 'listen' ? 'text answers' : 'reads answers out loud';
  return `${opens}, ${reads}, ${getVoiceLanguageLabel(voiceConfig.lang || VOICE_CONFIG_ON.lang)}`;
};
